import { BaseTable, CommandButton, JournalTable, Modal, TableHeader,
    JournalLinkedAccount, JournalTaxAccount, JournalProductByBSI, Loading
 } from "components"
import { Fragment} from "react"
import { convertNumber, useCoaItem, useToggle, useProductAvgPrice } from "utils"
import moment from "moment"
import type { FormReturnItemsType } from "../types"
import { useFormContext } from "react-hook-form"

export function Journal() {
    const { isActive, toggle } = useToggle(false)

    return (
        <Fragment>
            <CommandButton actiontype="journal" onClick={toggle} />

            {isActive && (
                <Modal isOpen title="JOURNAL" closeModal={toggle} size="full">
                    <Modal.Body>
                        <JournalTableSection />
                    </Modal.Body>
                </Modal>
            )}
        </Fragment>
    )
}

function JournalTableSection() {
    const { getValues } = useFormContext<FormReturnItemsType>()
    const transaction_date = getValues('transaction_date')
    const trx_code = getValues('trx_code')
    const detailTransaction = getValues('return_detail')
    // const [totalDebet, setTotalDebet] = useState<number>(0);
    // const [totalCredit, setTotalCredit] = useState<number>(0);

    return (
        <JournalTable>
            <JournalTable.Content>
                <BaseTable>
                    <TableHeader>
                        <tr>
                            <th className="w-[10px]">DATE</th>
                            <th>IDM</th>
                            <th>ACCOUNT</th>
                            <th>DEBET</th>
                            <th>CREDIT</th>
                        </tr>
                    </TableHeader>
                    <tbody>
                        {detailTransaction?.map((item, index) => {
                            const amount = item.quantity_returned * item.price_invoice * (100 - item.discount) / 100
                            // total_debet+=(amount+amountTax)
                            return (
                                <Fragment key={index}>
                                    {Boolean(amount) && (
                                        <>
                                            <JournalProduct 
                                                product={item}
                                                transaction_date={transaction_date}
                                                trx_code={trx_code ?? ''}                                                
                                                side="debet"
                                            />
                                        </>
                                    )}
                                </Fragment>
                            )
                        })}

                        {detailTransaction?.map((item, index) => {         
                            const amount = item.quantity_returned * item.price_invoice * (100 - item.discount) / 100
                            // total_credit+=(amount+amountTax)
                            return (
                                <Fragment key={index}>
                                    {Boolean(amount) && (
                                        <>   
                                            <JournalProduct 
                                                product={item}
                                                transaction_date={transaction_date}
                                                trx_code={trx_code ?? ''}                                                
                                                side="credit"
                                            />
                                        </>
                                    )}
                                </Fragment>
                            )
                        })}
                        {/* <tr className="font-bold border-t-[2px] border-black"> 
                            <td colSpan={3}>Total</td>
                            <td className="text-right">{convertNumber(totalDebet).intoCurrency}</td>
                            <td className="text-right">{convertNumber(totalCredit).intoCurrency}</td>
                        </tr> */}
                    </tbody>
                </BaseTable>
            </JournalTable.Content>
        </JournalTable>
    )
}

function JournalProduct(params: {
    product: any
    trx_code: string
    transaction_date: string
    side: string
}) {
    const amount = params.product.quantity_returned * params.product.price_invoice * (100 - params.product.discount) / 100
    const amountTax = params.product.tax_rate ? amount * params.product.tax_rate / 100 : 0
    const { data, isLoading } = useCoaItem(params.product.product_id)
    const { avg_price } = useProductAvgPrice(params.product.product_id)
    const amountCogs = params.product.quantity_unit / params.product.quantity_invoice * params.product.quantity_returned  * avg_price.avg_price_use

    const coa_buy_code = Boolean(data?.coa_buy_code && data.coa_buy_code !== "-")
    const coa_inv_code = Boolean(data?.coa_inv_code && data.coa_inv_code !== "-")
    const coa_sell_code = Boolean(data?.coa_sell_code && data.coa_sell_code !== "-")

    const date = moment(params.transaction_date).format("DD/MM/YYYY")

    if (isLoading) {
        return (
            <tr>
                <td colSpan={5}><Loading loading /></td>
            </tr>
        )
    }

    const rows: JSX.Element[] = []

    // DEBET SIDE
    if (params.side === "debet") {
        if ((coa_buy_code && coa_inv_code && coa_sell_code) || (!coa_buy_code && coa_inv_code && coa_sell_code)) {
            rows.push(
                <tr key="debet-sell">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalProductByBSI product_id={params.product.product_id.toString()} code="sell" />
                    <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                    <td></td>
                </tr>,
                <tr key="debet-inv">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalProductByBSI product_id={params.product.product_id.toString()} code="inv" />
                    <td className="text-right">{convertNumber(amountCogs).intoCurrency}</td>
                    <td></td>
                </tr>
            )
        } else if ((coa_buy_code && !coa_inv_code && coa_sell_code) || (!coa_buy_code && !coa_inv_code && coa_sell_code)) {
            rows.push(
                <tr key="debet-sell-only">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalProductByBSI product_id={params.product.product_id.toString()} code="sell" />
                    <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                    <td></td>
                </tr>
            )
        }

        if (params.product.tax_rate) {
            rows.push(
                <tr key="debet-tax">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalTaxAccount isPaid={false} tax_id={params.product.tax_id.toString()} />
                    <td className="text-right">{convertNumber(amountTax).intoCurrency}</td>
                    <td></td>
                </tr>
            )
        }

    } else { // CREDIT SIDE
        if ((coa_buy_code && coa_inv_code && coa_sell_code) || (!coa_buy_code && coa_inv_code && coa_sell_code)) {
            rows.push(
                <tr key="credit-la12">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalLinkedAccount coa_code="LA12" />
                    <td></td>
                    <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                </tr>,
                <tr key="credit-buy">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalProductByBSI product_id={params.product.product_id.toString()} code="buy" />
                    <td></td>
                    <td className="text-right">{convertNumber(amountCogs).intoCurrency}</td>
                </tr>
            )
        } else if ((coa_buy_code && !coa_inv_code && coa_sell_code) || (!coa_buy_code && !coa_inv_code && coa_sell_code)) {
            rows.push(
                <tr key="credit-la12-only">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalLinkedAccount coa_code="LA12" />
                    <td></td>
                    <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                </tr>
            )
        }

        if (params.product.tax_rate) {
            rows.push(
                <tr key="credit-tax">
                    <td>{date}</td>
                    <td>{params.trx_code}</td>
                    <JournalLinkedAccount coa_code="LA12" />
                    <td></td>
                    <td className="text-right">{convertNumber(amountTax).intoCurrency}</td>
                </tr>
            )
        }
    }

    return <>{rows}</>
}
